import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { STAGES, stageLabel, PRIORITIES, priorityCls, priorityBadgeCls, SOURCE_LABELS, fmtDate } from "./shared";
import LeadDrawer from "./LeadDrawer";
import { Plus, Search, Users, UserCheck, X, Handshake, CheckCircle2 } from "lucide-react";

export default function CrmLeads() {
    const [data, setData] = useState({ items: [], counts: {}, agents: [] });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", stage: "", type: "", agent: "", source: "", kind: "" });
    const [openLead, setOpenLead] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [showAgents, setShowAgents] = useState(false);

    const load = useCallback(async () => {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        try {
            const r = await api.get("/crm/leads", { params });
            setData(r.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const c = data.counts || {};

    return (
        <div data-testid="crm-tab">
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <MiniStat testId="crm-kpi-total" icon={<Users size={22} />} label="Contatti totali" value={c.total ?? 0} color="blue" />
                <MiniStat testId="crm-kpi-clients" icon={<UserCheck size={22} />} label="Clienti" value={c.clients ?? 0} color="green" />
                <MiniStat testId="crm-kpi-trattativa" icon={<Handshake size={22} />} label="In trattativa" value={c.trattativa ?? 0} color="amber" />
                <MiniStat testId="crm-kpi-conclusa" icon={<CheckCircle2 size={22} />} label="Concluse" value={c.conclusa ?? 0} color="violet" />
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap gap-3 items-center mb-6">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input data-testid="crm-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                        placeholder="Cerca nome, email o immobile..."
                        className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2.5 text-sm text-ink-50 focus:border-gold outline-none" />
                </div>
                <select data-testid="crm-filter-stage" value={filters.stage} onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
                    className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                    <option value="">Tutte le fasi</option>
                    {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select data-testid="crm-filter-type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                    <option value="">Lead + Clienti</option>
                    <option value="lead">Solo Lead</option>
                    <option value="client">Solo Clienti</option>
                </select>
                <select data-testid="crm-filter-kind" value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
                    className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                    <option value="">Acquirenti + Venditori</option>
                    <option value="buyer">Acquirenti</option>
                    <option value="seller">Venditori</option>
                </select>
                <select data-testid="crm-filter-source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                    className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                    <option value="">Tutte le origini</option>
                    {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {(data.agents || []).length > 0 && (
                    <select data-testid="crm-filter-agent" value={filters.agent} onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
                        className="bg-ink-950 border border-white/15 px-3 py-2.5 text-sm text-ink-50 outline-none">
                        <option value="">Tutti gli agenti</option>
                        {data.agents.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                )}
                {!data.collab && <button data-testid="crm-manage-agents" onClick={() => setShowAgents(true)} className="cta-outline px-4 py-2.5 text-xs">Agenti</button>}
                <button data-testid="crm-new-lead" onClick={() => setShowNew(true)} className="cta-gold px-4 py-2.5 text-xs flex items-center gap-2"><Plus size={14} /> Nuovo lead</button>
            </div>

            {/* Lead list */}
            <div className="hidden md:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider text-ink-400 px-5 py-2">
                <div className="col-span-3">Contatto</div>
                <div className="col-span-3">Immobile</div>
                <div className="col-span-2">Fase</div>
                <div className="col-span-1">Priorità</div>
                <div className="col-span-2">Agente</div>
                <div className="col-span-1 text-right">Ultima attività</div>
            </div>
            {loading && <div className="vd-card p-8 text-center text-ink-400 text-sm">Caricamento...</div>}
            {!loading && data.items.length === 0 && (
                <div className="vd-card p-10 text-center text-ink-400 text-sm">Nessun lead. Le richieste ricevute dai tuoi annunci appariranno qui automaticamente.</div>
            )}
            <div className="space-y-5">
                {data.items.map((l) => (
                    <button key={l.id} data-testid={`crm-lead-${l.id}`} onClick={() => setOpenLead(l.id)}
                        className="vd-card vd-lead-row no-pill w-full grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-5 py-4 text-left text-sm transition-colors">
                        <div className="col-span-2 md:col-span-3">
                            <div className="flex items-center gap-2">
                                <span className="text-ink-50 font-semibold truncate">{l.name}</span>
                                {l.type === "client" && <span className="vd-badge vd-badge-client">Cliente</span>}
                            </div>
                            <div className="text-[11px] text-ink-400 truncate">{l.email || SOURCE_LABELS[l.source] || ""}</div>
                        </div>
                        <div className="md:col-span-3 text-xs text-ink-400 truncate">{l.property_title || "—"}</div>
                        <div className="md:col-span-2"><span className="vd-badge vd-badge-stage">{stageLabel(l.stage)}</span></div>
                        <div className="md:col-span-1"><span className={priorityBadgeCls(l.priority)}>{l.priority}</span></div>
                        <div className="md:col-span-2 text-xs text-ink-400 truncate">{l.agent || "—"}</div>
                        <div className="md:col-span-1 text-[11px] text-ink-400 md:text-right">{fmtDate(l.last_activity_at).split(",")[0]}</div>
                    </button>
                ))}
            </div>

            {openLead && <LeadDrawer leadId={openLead} agents={data.agents || []} onClose={() => setOpenLead(null)} onChanged={load} />}
            {showNew && <NewLeadModal agents={data.agents || []} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
            {showAgents && <AgentsModal agents={data.agents || []} onClose={() => setShowAgents(false)} onSaved={() => { setShowAgents(false); load(); }} />}
        </div>
    );
}

function MiniStat({ icon, label, value, color = "blue", testId }) {
    const iconCls = color === "green" ? "vd-kpi-icon-highlight" : color === "amber" ? "vd-kpi-icon-amber" : color === "violet" ? "vd-kpi-icon-violet" : "";
    return (
        <div data-testid={testId} className="vd-kpi-card p-6 flex items-center gap-4">
            {icon && <span className={`vd-kpi-icon ${iconCls}`}>{icon}</span>}
            <div>
                <div className="text-ink-400 text-[10px] uppercase tracking-wider">{label}</div>
                <div className="font-serif text-2xl text-ink-50 mt-0.5">{value}</div>
            </div>
        </div>
    );
}

function NewLeadModal({ agents, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", priority: "media", agent: "", note: "", property_id: "", kind: "buyer" });
    const [props, setProps] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/me/properties").then((r) => setProps(r.data.items || [])).catch(() => {}); }, []);
    const submit = async () => {
        if (!form.name.trim()) return;
        setBusy(true);
        try {
            await api.post("/crm/leads", { ...form, agent: form.agent || null, property_id: form.property_id || null });
            onCreated();
        } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Nuovo lead" onClose={onClose}>
            <div className="space-y-3">
                <SelectField testid="new-lead-kind" label="Tipo contatto" value={form.kind} onChange={(v) => setForm({ ...form, kind: v })}
                    options={[["buyer", "Acquirente / affittuario"], ["seller", "Venditore (acquisizione immobile)"]]} />
                <Input testid="new-lead-name" label="Nome *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <div className="grid grid-cols-2 gap-3">
                    <Input testid="new-lead-email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                    <Input testid="new-lead-phone" label="Telefono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <SelectField label="Priorità" value={form.priority} onChange={(v) => setForm({ ...form, priority: v })}
                        options={PRIORITIES.map((p) => [p.id, p.label])} />
                    <SelectField label="Agente" value={form.agent} onChange={(v) => setForm({ ...form, agent: v })}
                        options={[["", "—"], ...agents.map((a) => [a, a])]} />
                </div>
                <SelectField label="Immobile collegato" value={form.property_id} onChange={(v) => setForm({ ...form, property_id: v })}
                    options={[["", "—"], ...props.map((p) => [p.id, p.title])]} />
                <div>
                    <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">Nota iniziale</label>
                    <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                </div>
                <button data-testid="new-lead-submit" onClick={submit} disabled={busy || !form.name.trim()} className="cta-gold w-full py-3 text-sm disabled:opacity-40">
                    {busy ? "Creazione..." : "Crea lead"}
                </button>
            </div>
        </Modal>
    );
}

function AgentsModal({ agents, onClose, onSaved }) {
    const [list, setList] = useState(agents);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await api.put("/crm/agents", { agents: list }); onSaved(); }
        catch (e) { console.error(e); }
        finally { setBusy(false); }
    };
    return (
        <Modal title="Agenti dell'agenzia" onClose={onClose}>
            <p className="text-xs text-ink-400 mb-4">Aggiungi i nomi dei tuoi agenti per assegnarli ai lead.</p>
            <div className="flex gap-2 mb-4">
                <input data-testid="agent-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) { setList([...list, name.trim()]); setName(""); } }}
                    placeholder="Es. Mario Rossi" className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
                <button data-testid="agent-add" onClick={() => { if (name.trim()) { setList([...list, name.trim()]); setName(""); } }} className="cta-outline px-4 text-xs">Aggiungi</button>
            </div>
            <div className="flex flex-wrap gap-2 mb-6 min-h-[32px]">
                {list.map((a, i) => (
                    <span key={`${a}-${i}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-gold/40 bg-gold/5 text-ink-50">
                        {a}<button onClick={() => setList(list.filter((_, j) => j !== i))} className="text-ink-400 hover:text-red-300"><X size={11} /></button>
                    </span>
                ))}
                {list.length === 0 && <span className="text-xs text-ink-500">Nessun agente</span>}
            </div>
            <button data-testid="agents-save" onClick={save} disabled={busy} className="cta-gold w-full py-3 text-sm">{busy ? "Salvataggio..." : "Salva"}</button>
        </Modal>
    );
}

export function Modal({ title, onClose, children }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-ink-950 border border-white/15 p-6 max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-xl text-ink-50">{title}</h3>
                    <button data-testid="modal-close" onClick={onClose} className="text-ink-400 hover:text-ink-50"><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

export function Input({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">{label}</label>
            <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-ink-50 focus:border-gold outline-none" />
        </div>
    );
}

export function SelectField({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-[10px] text-ink-400 uppercase tracking-wider mb-1">{label}</label>
            <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-ink-950 border border-white/15 px-3 py-2 text-sm text-ink-50 outline-none">
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </div>
    );
}
